// @ts-strict-ignore
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import queryString from 'query-string';
import stableStringify from 'json-stable-stringify';

import {isBrowserEnv, isDevEnv, isStandalone, isTestEnv} from 'common/report/util/env';
import {parseUrl} from 'common/util/url';
import {getPublicDashboardUuid} from 'common/report/util/public/uuid-helpers';
import {NamedError} from 'common/report/queries/utils/named-error';
import {filterSearchParamsForCache, fromFetchCache, updateFetchCache} from 'common/report/queries/utils/fetch-cache';
import {getProjectId, getWorkspaceId} from 'common/report/util';

export interface FetchOptions {
  /**
   * request's method
   */
  method?: `GET` | `POST` | `PUT` | `PATCH` | `DELETE`;
  /**
   * request's headers as a property bag
   */
  headers?: {[headerName: string]: string};
  /**
   * request's body. NOTE: this is only valid for POST/PUT/PATCH requests
   */
  body?: string | FormData;
  /**
   * extra params to send as part of underlying fetch(url, params) call
   */
  params?: Partial<RequestInit>;
  /**
   * throw ResponseError if status > 400.
   * NOTE: using .get/.post/.put/.patch/.delete will always throw for invalid responses
   * only pass throwOnInvalidStatus if calling .fetch directly
   */
  throwOnInvalidStatus?: boolean;
  /**
   * abort signal from an AbortController.
   * calling .abort() of controller will abort the request with AbortError
   */
  abortSignal?: AbortSignal;
  /**
   * query from fetch-cache, don't use network.
   * Note: will return Promise<null> if match fails.
   */
  fromFetchCache?: boolean;
  /**
   * if fetch-cache response is older than ttl seconds, Promise<null> will be returned.
   * used in conjuction when fromFetchCache:true is set or calling .hydratedGet helper.
   */
  fromFetchCacheTtl?: number;
  /**
   * when the network response comes back, update fetch-cache so it can be queried next time with fromFetchCache.
   */
  updateFetchCache?: boolean;

  /**
   * if the endpoint is public, we don't need to fetch the access token (example: signup)
   */
  skipAccessToken?: boolean;

  /**
   * sets a minimum ms duration for a successful promise.
   * This is useful for flashing load screens caused by an endpoint responding too quickly.
   */
  minExecutionTime?: number;
  importance?: `low` | `high` | `auto`;
}

export type SuccessResponse<ResultsType> = {
  metadata?: any;
  results: ResultsType;
  status: `ok`;
};

export type ErrorResponse = {
  status: `error`;
  error: string;
  type?: string;
};

export type ApiResponse<ResultsType> = ErrorResponse | SuccessResponse<ResultsType>;

export const enum StatusCode {
  Ok = 200,
  Created = 201,
  NoContent = 204,
  BadRequest = 400,
  Unauthenticated = 401,
  PermissionDenied = 403,
  NotFound = 404,
  MethodNotAllowed = 405,
  Conflict = 409,
  TooManyRequests = 429,
  HeaderFieldsTooLarge = 431,
  InternalServerError = 500,
  NotImplemented = 501,
  BadGateway = 502,
  ServiceUnavailable = 503,
  GatewayTimeout = 504,
}

/** An application-level error that may contain a meaningful error message. */
export class ApiResponseError extends NamedError {
  declare response: ApiResponse<any>;
  declare message: string;
  declare statusCode: number;

  constructor(response: ApiResponse<any>, message: string, statusCode: number = null) {
    super(message);
    this.response = response;
    this.statusCode = statusCode;
  }
}

/** An HTTP-level error that doesn't contain a body. */
export class ResponseError extends Error {
  declare response: Response;
  declare message: string;

  constructor(response: Response, message: string = null) {
    super(message);
    this.response = response;
    this.message = message || `${response.status} - ${this.getMessage()} (${response.url})`;
  }

  /**
   * Use response status to return a slightly more user-friendly error message.
   */
  getMessage(): string {
    return this.message;
  }
}

const STATUS_TO_MESSAGE = new Map([
  // OK is only an error if it's not JSON-able
  [StatusCode.Ok, `Expected JSON response body`],
  [StatusCode.BadRequest, `Bad request`],
  [StatusCode.Unauthenticated, `Unauthenticated`],
  [StatusCode.PermissionDenied, `Permission denied or report contains data that has been marked classified`],
  [StatusCode.NotFound, `Resource not found`],
  [StatusCode.MethodNotAllowed, `Request method not allowed`],
  [StatusCode.TooManyRequests, `Too many requests - try again later`],
  [
    StatusCode.HeaderFieldsTooLarge,
    `Request header fields too large - try clearing your cookies and retry the request again`,
  ],
  [StatusCode.InternalServerError, `Internal server error`],
  [StatusCode.NotImplemented, `Not implemented`],
  [StatusCode.BadGateway, `Bad gateway`],
  [StatusCode.ServiceUnavailable, `Service unavailable`],
  [StatusCode.GatewayTimeout, `Gateway timeout`],
]);

export function makeError(response: Response): ResponseError {
  const message = STATUS_TO_MESSAGE.get(response.status) ?? `Request failed`;
  return new ResponseError(response, `${response.status} - ${message}`);
}

export function apiHost() {
  if (isTestEnv() && typeof window[`API_ORIGIN_OVERRIDE`] !== `undefined`) {
    // so node unit tests can cover cross origin apiHost() related logic
    return window[`API_ORIGIN_OVERRIDE`];
  } else if (typeof API_ORIGIN_OVERRIDE !== `undefined` && API_ORIGIN_OVERRIDE) {
    return API_ORIGIN_OVERRIDE;
  } else if (isStandalone() && !isTestEnv() && window.location.hostname === `localhost`) {
    // only localhost standalone dev mode needs cross origin calls to https://mixpanel.com
    // other hosts e.g x.devbox.mixpanel.org, staging-x.mixpanel.com, (eu.)?mixpanel.com implement /api/app endpoints
    // design.mixpanel.com mocks out all fetch calls
    return `https://mixpanel.com`;
  } else {
    return ``;
  }
}

export const isSameOrigin = () => isBrowserEnv() && apiHost() === ``;

async function handleErrors<ResultsType>(response: Response): Promise<SuccessResponse<ResultsType>> {
  // handle null response from cache miss
  if (response === null || response.status === StatusCode.NoContent) {
    return null;
  }

  // When our app api endpoints return a response, we always return them as json.
  // However, we need to handle cases where the app api endpoint is not controlling the response
  // such as a 404, 500 or other unforeseen errors.
  let responseJson: ApiResponse<ResultsType>;
  try {
    responseJson = await response.json();
  } catch {
    throw makeError(response);
  }

  // Check the response status. If there was an error, throw it as an Error
  switch (responseJson.status) {
    case `ok`: {
      return responseJson as SuccessResponse<ResultsType>;
    }
    case `error`: {
      // We expect `error` to always be a string but we have more work to do to enforce it
      // programmatically. Some existing APIs might send back a json object instead of a string in
      // which case we'll JSON.stringify it.
      const message = stringifyIfObj(responseJson.error);
      throw new ApiResponseError(responseJson, message, response.status);
    }
    default:
      throw new ApiResponseError(
        responseJson,
        // @ts-ignore Property 'status' does not exist on type 'never'
        `Invalid api response. status should be either 'ok' or 'error', got '${responseJson.status}'`,
        response.status,
      );
  }
}

// TODO: Stop unwrapping results and return entire response object.
async function resultsFromResponse<ResultsType>(response: Response): Promise<ResultsType> {
  return handleErrors<ResultsType>(response).then((r) => (r ? r.results : null));
}

function stringifyIfObj(data: string | Record<string, any>): string {
  if (data instanceof Object) {
    return JSON.stringify(data);
  }
  return data;
}

const RESPONSE_TRACKING_PROPS = [`redirected`, `status`];
const APP_API_REGEXP = /^\/api\/app\/(.+)/;
const QUERY_API_REGEXP = /^\/api\/query\/(.+)/;
const PUBLIC_API_REGEXP = /^\/api\/app\/public\/(.+)/;

interface NetworkTrackingProps {
  host: string;
  pathname: string;
  url: string;
  api_type?: string;
  endpoint?: string;
}

function trackingPropsFromUrl(url: string) {
  const {host, pathname} = parseUrl(url);
  const props: NetworkTrackingProps = {url, host, pathname};

  let pathMatch;
  if ((pathMatch = PUBLIC_API_REGEXP.exec(pathname))) {
    props.api_type = `public-api`;
  } else if ((pathMatch = APP_API_REGEXP.exec(pathname))) {
    props.api_type = `app-api`;
  } else if ((pathMatch = QUERY_API_REGEXP.exec(pathname))) {
    props.api_type = `query-api`;
  }
  if (pathMatch) {
    props.endpoint = pathMatch[1].replace(/\/\d+(\/|$)/g, `/<id>/`).replace(/\/$/, ``);
  }

  return props;
}

/**
 * Send timing and other info for each API call from the front-end
 * to the Infra Metrics project, for correlation of latency with ARR etc.
 */
export function trackApiClientFetch(
  response: {url: string; redirected: boolean; status: number},
  {latencyMs, cached, method}: {latencyMs: number; cached: boolean; method: string},
  additionalTrackingDict: Obj = {},
) {
  // MP_TRACKING_SEND_DATA is inlined by webpack, under test it is explicitly inlined as "false",
  // which gives us no dynamic way to test it. The TESTING_TRACKING_WITH_MOCK_TRACK global value
  // is ONLY set when trying to test this function.
  if (!MP_TRACKING_SEND_DATA && !globalThis.TESTING_TRACKING_WITH_MOCK_TRACK) {
    return;
  }

  const projectId = getProjectId();
  const trackingProps = {
    ...trackingPropsFromUrl(response.url),
    ...pick(response, RESPONSE_TRACKING_PROPS),
    latency_ms: latencyMs,
    cached,
    method,
    project_id: projectId,
    workspace_id: getWorkspaceId() || undefined,
    ...additionalTrackingDict,
  };
  mixpanel.track(`Browser API fetch`, trackingProps);
  mixpanel.infra_metrics?.track(`browser-api-fetch`, {
    distinct_id: projectId || getPublicDashboardUuid(),
    ...trackingProps,
  });
}

/**
 * setting location.href = url is not a blocking operation, rest of code continues to run until browser unloads.
 * this helper function sets location.href and returns a never resolving promise which means an await will block until
 * browser switches to new page.
 */
export function waitForRedirect(url: string): Promise<void> {
  if (isDevEnv()) {
    console.info(`redirecting to`, url);
  }
  window.location.href = url;
  return new Promise(() => ``);
}

export class HttpClient {
  protected declare pendingResponseCache: Map<string, Promise<any>>;

  constructor() {
    this.pendingResponseCache = new Map();
  }

  public async get<ResultsType>(
    url: string,
    query: {[key: string]: any} = null,
    options: FetchOptions = {},
    queryStringArrayFormat = `none`,
  ): Promise<ResultsType> {
    const apiUrl = query ? `${url}?${queryString.stringify(query, {arrayFormat: queryStringArrayFormat})}` : url;
    const response = await this.fetch(apiUrl, {method: `GET`, ...options});
    return resultsFromResponse(response);
  }

  public async post<ResultsType>(
    url: string,
    data: string | Record<string, any> | FormData,
    options: FetchOptions = {},
  ): Promise<ResultsType> {
    const response = await this.fetch(url, {
      method: `POST`,
      body: data instanceof FormData ? data : stringifyIfObj(data),
      ...options,
    });
    return resultsFromResponse(response);
  }

  public async postNoUnwrap<ResultsType>(
    url: string,
    data: string | Record<string, any> | FormData,
    options: FetchOptions = {},
  ): Promise<SuccessResponse<ResultsType>> {
    const response = await this.fetch(url, {
      method: `POST`,
      body: data instanceof FormData ? data : stringifyIfObj(data),
      ...options,
    });
    return handleErrors(response);
  }

  public async put<ResultsType>(
    url: string,
    data: string | Record<string, any>,
    options: FetchOptions = {},
  ): Promise<ResultsType> {
    const response = await this.fetch(url, {method: `PUT`, body: stringifyIfObj(data), ...options});
    return resultsFromResponse(response);
  }

  public async patch<ResultsType>(
    url: string,
    data: string | Record<string, any>,
    options: FetchOptions = {},
  ): Promise<ResultsType> {
    const response = await this.fetch(url, {method: `PATCH`, body: stringifyIfObj(data), ...options});
    return resultsFromResponse(response);
  }

  public async delete<ResultsType>(url: string, options: FetchOptions = {}): Promise<ResultsType> {
    const response = await this.fetch(url, {method: `DELETE`, ...options});
    return resultsFromResponse(response);
  }

  /**
   * queries fetch-cache if a cached response is available.
   * in parallel, also queries the network and updates (hydrates) the cache when network responds.
   * @example
   *  const {cache, network} = apiClient.hydratedGet(url, options);
   *  this.update({loading: true});
   *  const cacheResult = await cache;
   *  if (cacheResult !== null) {
   *    this.update({result: cachedResult, loading: false, fromCache: true});
   *  }
   *  const networkResult = await network;
   *  this.update({result: networkResult, fromCache: false});
   */
  public hydratedGet<ResultsType>(
    url: string,
    query: {[key: string]: any} = null,
    options: FetchOptions = {},
  ): {cache: Promise<ResultsType>; network: Promise<ResultsType>} {
    // according to rest principles, GET should not modify data on server, it's a pure call
    // query-api passes POST for GET like calls e.g for insights api because it expects params as a json tree
    // we make an exception and allow hydratedGet to be allowed to be called with POST for query-api.
    return options.method === `POST` && /\/api\/2.0\//.test(url)
      ? {
          cache: this.post<ResultsType>(url, query, {...options, fromFetchCache: true}),
          network: this.post<ResultsType>(url, query, {...options, updateFetchCache: true}),
        }
      : {
          cache: this.get<ResultsType>(url, query, {...options, fromFetchCache: true}),
          network: this.get<ResultsType>(url, query, {...options, updateFetchCache: true}),
        };
  }

  public async fetch(url: string, options: FetchOptions = {}): Promise<Response> {
    return this._fetch(url, options);
  }

  protected _urlWithApiHost(url: string) {
    return url.startsWith(`/`) ? `${apiHost()}${url}` : url;
  }

  // TODO opslead
  protected async _fetch(url: string, fetchOptions: FetchOptions = {}): Promise<Response> {
    const startTime = new Date().getTime();

    url = this._urlWithApiHost(url);
    const {
      headers = {},
      method = `GET`,
      body = null,
      params = {},
      throwOnInvalidStatus = false,
      abortSignal: signal = null,
    } = fetchOptions || {};

    const fetchInit: RequestInit = {
      headers,
      method,
      body,
      signal,
      ...params,
    };

    const pendingResponseCacheKey = stableStringify({url, fetchInit, fromFetchCache: fetchOptions.fromFetchCache});
    if (this.pendingResponseCache.has(pendingResponseCacheKey)) {
      // we clone the response because body can only be read once.
      // multiple callers calling response.json() will fail with 'body stream is locked' error
      return (
        this.pendingResponseCache
          .get(pendingResponseCacheKey)
          // return fetch Cache miss as null
          .then((response) => (response ? response.clone() : null))
      );
    }

    let responsePromise: Promise<Response>;
    if (fetchOptions.fromFetchCache) {
      responsePromise = fromFetchCache(filterSearchParamsForCache(url), fetchInit, fetchOptions.fromFetchCacheTtl)
        .then(({cachedResponse, trackingDict}) => {
          if (cachedResponse) {
            const endTime = new Date().getTime();
            trackApiClientFetch(cachedResponse, {latencyMs: endTime - startTime, cached: true, method}, trackingDict);
          }
          return cachedResponse;
        })
        .finally(() => this.pendingResponseCache.delete(pendingResponseCacheKey));
    } else {
      responsePromise = window
        .fetch(url, fetchInit)
        .then(async (response) => {
          const endTime = new Date().getTime();
          trackApiClientFetch(response, {latencyMs: endTime - startTime, cached: false, method});

          if (response.status === StatusCode.NotFound) {
            // HTTP hack: 302 API responses across our US/EU subdomains will
            // get rejected by browser before ApiClient can follow them,
            // so instead we return 404 with a custom reponse header to
            // tell ApiClient where to switch the page to
            const redirectLocation = response.headers.get(`mp-base-location`);
            if (redirectLocation) {
              await waitForRedirect(redirectLocation);
              return response;
            }
          }

          if (throwOnInvalidStatus && response.status >= StatusCode.BadRequest) {
            throw makeError(response);
          }

          if (response.status >= StatusCode.InternalServerError) {
            // we always throw for 500-ish error
            throw makeError(response);
          } else if (response.status === StatusCode.Unauthenticated) {
            throw new ResponseError(response, `Authorization failed`);
          } else if (response.redirected) {
            await waitForRedirect(response.url);
            return response;
          } else {
            // underlying updateFetchCache will only update if response.ok and response.status === 200
            if (fetchOptions.updateFetchCache) {
              updateFetchCache(filterSearchParamsForCache(url), omit(fetchInit, `headers.Authorization`), response);
            }
            return response;
          }
        })
        .finally(() => this.pendingResponseCache.delete(pendingResponseCacheKey));
    }

    this.pendingResponseCache.set(pendingResponseCacheKey, responsePromise);
    return responsePromise;
  }
}
